import React from 'react';

import Image from 'next/image';
import BlogCategoryList from '../../../components/blog-category-list';
import HeroSubpage from '../../../components/hero-subpage';
import Layout from '../../../components/layout';
import BlogArticleSuggestion from '../../../components/blog-article-suggestion';
import { fetchAPI } from '../../../lib/api';
import SeoContext from '../../../lib/seo-context';
import { Merge, estimateTime, formatDate } from '../../../lib/helper';
import BlogArticleLabels from '../../../components/blog-article-labels';
import ShareIcons from '../../../components/share-icons';

function Article(props) {
    const { pageProps, global } = props;
    if (!pageProps) return;
    const nextArticle = pageProps.nextArticle ? pageProps.nextArticle : null;
    const prevArticle = pageProps.prevArticle ? pageProps.prevArticle : null;
    const categories = pageProps.categories ? pageProps.categories : null;
    const article = pageProps;

    const seo = pageProps && pageProps.seo ? Merge(global.seo, pageProps.seo) : global.seo;

    seo.canonicalURL = `${process.env.NEXT_PUBLIC_SITE_URL}/blog/articles/${pageProps.slug}`;
    seo.type = 'article';
    return (
        <SeoContext.Provider value={seo}>
            <Layout
                footerClass={'skew-maturity-100'}
                languageData={{ originalSlug: pageProps.originalSlug, languages: pageProps.localizations }}
            >
                <HeroSubpage content={``}></HeroSubpage>
                <section className="container max-w-screen-xl mx-auto pt-10 relative z-10">
                    <div className="flex flex-col lg:flex-row px-10 xl:px-0">
                        <div className="flex flex-col w-full lg:w-2/3 relative">
                            {article && (
                                <>
                                    <article>
                                        <h1 className="text-[2rem] text-trust-500">{article.title}</h1>
                                        <BlogArticleLabels
                                            title={article.blog_category.data.attributes.title}
                                            estimate={article.estimate}
                                            date={article.date}
                                        ></BlogArticleLabels>

                                        <div className="relative flex w-full aspect-video mb-4 rounded-md overflow-hidden">
                                            <Image
                                                layout="fill"
                                                src={article.image.data.attributes.url}
                                                alt={article.image.data.attributes.alternativeText}
                                                priority
                                            />
                                        </div>
                                        <div className="flex flex-col md:flex-row relative">
                                            <div className="flex flex-col pr-4 md:pb-4">
                                                <div className="share-icons sticky top-32 pt-2">
                                                    <ShareIcons
                                                        title={article.title}
                                                        url={`/blog/articles/${article.slug}`}
                                                        body={article.summary}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex flex-col flex-grow pb-2">
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: article.content
                                                    }}
                                                ></span>
                                            </div>
                                        </div>
                                    </article>
                                </>
                            )}
                        </div>
                        <div className="flex flex-row flex-wrap w-full lg:flex-col lg:w-1/3 lg:pl-10 lg:mt-20 relative">
                            <div className="flex flex-row lg:flex-col flex-wrap sticky top-32 w-full">
                                {categories && <BlogCategoryList selected="none" categories={categories} />}
                                {prevArticle && (
                                    <BlogArticleSuggestion article={prevArticle} direction="left" />
                                )}
                                {nextArticle && (
                                    <BlogArticleSuggestion article={nextArticle} direction="right" />
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </SeoContext.Provider>
    );
}

export default Article;

export async function getStaticPaths({ locales }) {
    const paths = [];
    await Promise.all(
        locales.map(async (locale) => {
            const results = await fetchAPI('/blog-articles', { locale, populate: 'deep,1' });

            return results.data.map((page) => {
                return paths.push({
                    params: {
                        slug: page.attributes.slug
                    },
                    locale
                });
            });
        })
    );

    return {
        paths,
        fallback: true
    };
}
const fetchSuggestedArticlesData = async (filters, locale) => {
    const result = await fetchAPI(`/blog-articles`, {
        populate: 'deep,2',
        locale,
        sort: `${filters && filters.id?.['$lt'] ? 'id:desc' : 'id:asc'}`,
        pagination: { limit: 1 },
        filters
    });

    return result.data.length ? result.data[0].attributes : null;
};
export async function getStaticProps({ params, locale }) {
    try {
        const result = await fetchAPI(`/blog-articles/by-slug/${params.slug}`, {
            populate: 'deep,3',
            locale
        });

        if (!result.data) {
            return { notFound: true };
        }

        result.data.attributes.estimate = estimateTime(result.data.attributes.content);
        result.data.attributes.date = formatDate(result.data.attributes.createdAt);
        result.data.attributes.nextArticle = null;
        result.data.attributes.prevArticle = null;
        const categoriesData = await fetchAPI(`/blog-categories`, {
            populate: 'deep,2',
            sort: ['title:asc'],
            locale: locale
        });

        const categories = [];
        categoriesData.data.map((cat) => {
            categories.push({
                id: cat.id,
                slug: cat.attributes.slug,
                title: cat.attributes.title,
                description: cat.attributes.title,
                seo: cat.attributes.seo,
                articleCount: cat.attributes.blog_articles.data.length
            });
        });

        result.data.attributes.categories = categories;

        // get next article suggestion
        const nextArticle = await fetchSuggestedArticlesData({ id: { $lt: result.data.id } }, locale);

        if (nextArticle) {
            nextArticle.estimate = estimateTime(nextArticle.content);
            nextArticle.date = formatDate(nextArticle.createdAt);
            result.data.attributes.nextArticle = nextArticle;
        }

        // get previous article suggestion
        const prevArticle = await fetchSuggestedArticlesData({ id: { $gt: result.data.id } }, locale);

        if (prevArticle) {
            prevArticle.estimate = estimateTime(prevArticle.content);
            prevArticle.date = formatDate(prevArticle.createdAt);
            result.data.attributes.prevArticle = prevArticle;
        }

        return {
            props: { pageProps: result.data.attributes },
            revalidate: Number(process.env.NEXT_PUBLIC_PAGE_REVALIDATION) || 100
        };
    } catch (e) {
        console.log('got an error', e.message);
        return { notFound: true };
    }
}
